import React, { useEffect, useState, useCallback } from "react";
import {
    Box,
    Typography,
    CircularProgress,
    Paper,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Tooltip,
    Checkbox,
    IconButton,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import Layout from "../../components/Layout";
import config from "../../Config";
import { useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import dayjs from "dayjs";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SnackbarNotification from "../../components/SnackbarNotification";
import styles from "./TokensPage.styles";
import { isLoggedIn } from "../../utils/utils";

const CustomNoRowsOverlay = () => (
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 200 }}>
        <Typography variant="body2" sx={{ color: "gray" }}>
            No tokens found. Please generate a token to get started.
        </Typography>
    </Box>
);

const TokensPage = () => {
    const [tokens, setTokens] = useState([]);
    const [loading, setLoading] = useState(true);
    const [generatedToken, setGeneratedToken] = useState("");
    const [generateLoading, setGenerateLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [updatingPrimary, setUpdatingPrimary] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    const navigate = useNavigate();

    const fetchTokens = useCallback(async () => {
        const user = JSON.parse(localStorage.getItem("user"));
        if (!user?.token) return;

        setLoading(true);
        try {
            const response = await fetch(`${config.apiBaseUrl}/token/statistic`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "user-authorization": user.token,
                },
            });

            if (!response.ok) {
                const errorResponse = await response.json();
                setSnackbar({ open: true, message: `Failed to fetch tokens: ${errorResponse.message}`, severity: "error" });
                return;
            }

            const data = await response.json();
            const formattedTokens = Array.isArray(data)
                ? data.map((token) => ({
                    ...token,
                    expires_at: token.expires_at ? dayjs(token.expires_at).format("YYYY-MM-DD HH:mm:ss") : "N/A",
                    created_at: token.created_at ? dayjs(token.created_at).format("YYYY-MM-DD HH:mm:ss") : "N/A",
                    last_used_at: token.last_used_at ? dayjs(token.last_used_at).format("YYYY-MM-DD HH:mm:ss") : "Never Used",
                    is_primary: token.is_primary || false,
                }))
                : [];
            setTokens(formattedTokens);
        } catch (error) {
            Sentry.captureException(error);
            setSnackbar({ open: true, message: "Error fetching tokens.", severity: "error" });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!isLoggedIn()) {
            navigate("/login");
            return;
        }
        (async () => {
            await fetchTokens();
        })();
    }, [fetchTokens, navigate]);

    const handleGenerateToken = async () => {
        const user = JSON.parse(localStorage.getItem("user"));
        if (!user?.token) return;

        setGenerateLoading(true);
        setGeneratedToken("");

        try {
            const response = await fetch(`${config.apiBaseUrl}/token`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "user-authorization": user.token,
                },
                body: JSON.stringify({ plan: "Trial" }),
            });

            if (!response.ok) {
                const errorResponse = await response.json();
                setSnackbar({ open: true, message: `Failed to generate token: ${errorResponse.message}`, severity: "error" });
                return;
            }

            const data = await response.json();
            setGeneratedToken(data.token);
            setOpenDialog(true);
        } catch (error) {
            Sentry.captureException(error);
            setSnackbar({ open: true, message: "Error generating token.", severity: "error" });
        } finally {
            setGenerateLoading(false);
        }
    };

    const handleRegenerateToken = async (tokenId) => {
        const user = JSON.parse(localStorage.getItem("user"));
        if (!user?.token) return;

        setGenerateLoading(true);
        setGeneratedToken("");

        try {
            const response = await fetch(`${config.apiBaseUrl}/token/recreate/${tokenId}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "user-authorization": user.token,
                }
            });

            if (!response.ok) {
                const errorResponse = await response.json();
                setSnackbar({ open: true, message: `Failed to regenerate token: ${errorResponse.message}`, severity: "error" });
                return;
            }

            const data = await response.json();
            setGeneratedToken(data.token);
            setOpenDialog(true);
        } catch (error) {
            Sentry.captureException(error);
            setSnackbar({ open: true, message: "Error regenerating token.", severity: "error" });
        } finally {
            setGenerateLoading(false);
        }
    };

    const handleSetPrimary = async (id) => {
        if (updatingPrimary) return;

        const user = JSON.parse(localStorage.getItem("user"));
        if (!user?.token) return;

        setUpdatingPrimary(true);
        try {
            const response = await fetch(`${config.apiBaseUrl}/token/primary?token_id=${id}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "user-authorization": user.token,
                }
            });

            if (!response.ok) {
                const errorResponse = await response.json();
                setSnackbar({ open: true, message: `Failed to update primary token: ${errorResponse.message}`, severity: "error" });
                return;
            }

            setSnackbar({ open: true, message: "Primary token updated successfully!", severity: "success" });
            await fetchTokens();
        } catch (error) {
            Sentry.captureException(error);
            setSnackbar({ open: true, message: "Error updating primary token.", severity: "error" });
        } finally {
            setUpdatingPrimary(false);
        }
    };

    const handleCopyToClipboard = async () => {
        await navigator.clipboard.writeText(generatedToken);
        setSnackbar({ open: true, message: "Token copied to clipboard!", severity: "success" });
    };

    const handleCloseDialog = async () => {
        setOpenDialog(false);
        await fetchTokens();
    };

    if (loading) {
        return (
            <Layout>
                <Box sx={styles.loadingContainer}>
                    <CircularProgress />
                </Box>
            </Layout>
        );
    }

    const columns = [
        {
            field: "is_primary",
            headerName: "Primary",
            width: 100,
            renderCell: (params) => (
                <Tooltip title={params.value ? "Primary token" : "Set as primary token"}>
                    <Checkbox
                        checked={params.value}
                        disabled={params.value}
                        onChange={() => {
                            if (!params.value) {
                                handleSetPrimary(params.row.id);
                            }
                        }}
                        color="primary"
                    />
                </Tooltip>
            )
        },
        // { field: "id", headerName: "Token ID", width: 300 },
        { field: "plan", headerName: "Plan", width: 150 },
        { field: "points", headerName: "Points", width: 100, type: "number" },
        { field: "expires_at", headerName: "Expires At", width: 200 },
        { field: "created_at", headerName: "Created At", width: 200 },
        { field: "last_used_at", headerName: "Last Used At", width: 200 },
        {
            field: "actions",
            headerName: "",
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            width: 80,
            align: "right",
            renderCell: (params) => (
                <Tooltip title="Regenerate Token">
                    <IconButton
                        onClick={() => handleRegenerateToken(params.row.id)}
                        disabled={generateLoading}
                        size="small"
                    >
                        <AutorenewIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            ),
        },
    ];

    const rows = tokens.map((token) => ({
        id: token.id,
        plan: token.plan,
        points: token.points,
        is_primary: token.is_primary,
        expires_at: token.expires_at,
        created_at: token.created_at,
        last_used_at: token.last_used_at,
    }));

    const sortedRows = [...rows].sort((a, b) => {
        if (a.is_primary && !b.is_primary) return -1;
        if (!a.is_primary && b.is_primary) return 1;
        return 0;
    });

    return (
        <Layout>
            <Box sx={{ width: "100%", px: 2, mt: 0 }}>
                <Box sx={styles.pageHeader}>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>Tokens</Typography>
                    <Typography variant="body1" sx={{ color: "rgba(0, 0, 0, 0.6)" }}>Home • Tokens</Typography>
                </Box>

                {tokens.length === 0 && (
                    <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleGenerateToken}
                            disabled={generateLoading}
                        >
                            {generateLoading ? "Generating..." : "Generate Token"}
                        </Button>
                    </Box>
                )}

                <Typography variant="h6" sx={{ fontWeight: 500, mb: 0 }}>Your Tokens</Typography>

                <Paper elevation={3} sx={styles.dataGridPaper}>
                    <DataGrid
                        rows={sortedRows}
                        columns={columns}
                        initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
                        pageSizeOptions={[5, 10, 25]}
                        disableRowSelectionOnClick
                        components={{
                            NoRowsOverlay: CustomNoRowsOverlay,
                        }}
                    />
                </Paper>
            </Box>

            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    Token Generated
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ color: "red", mb: 2 }}>
                        <strong>IMPORTANT:</strong> Please save your token in a safe place.
                    </Typography>
                    <TextField
                        value={generatedToken}
                        variant="outlined"
                        fullWidth
                        multiline
                        rows={6}
                        disabled
                        sx={styles.dialogTextField}
                    />
                </DialogContent>
                <DialogActions sx={{ justifyContent: "flex-end" }}>
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleCopyToClipboard}
                        startIcon={<ContentCopyIcon />}
                    >
                        Copy Token
                    </Button>
                </DialogActions>
            </Dialog>

            <SnackbarNotification
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            />
        </Layout>
    );
};

export default TokensPage;
